import React, { FC, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { getLinkedFirm } from "../../sessionStorage/sessionStorage";
import { useBulkSetClientTeamMutation } from "../../slices/ClientTeamSlice";
import { useGetUsersQuery } from "../../slices/UserSlice";
import {
  errorText,
  modalCancel,
  modalPrimary,
  PortalModal,
} from "../clientPortal/PortalModal";
import { bulkSummary } from "./clientTeam.utils";
import {
  ClientTeamInfoButton,
  pickerClass,
  sortedTeam,
  userName,
} from "./ClientTeamParts";

/** "Keep as is" = this side isn't changed for the selected clients. */
const KEEP = "__keep";

/** Contacts list bulk action "Set client team" for the selected clients. */
export const BulkClientTeamModal: FC<{
  customerIds: string[];
  onClose: () => void;
  onDone?: () => void;
}> = ({ customerIds, onClose, onDone }) => {
  const orgId = getLinkedFirm()?.orgId || "";
  const { data: users } = useGetUsersQuery(orgId, { skip: !orgId });
  const team = useMemo(() => sortedTeam(users), [users]);
  const [owner, setOwner] = useState(KEEP);
  const [manager, setManager] = useState(KEEP);
  const [problem, setProblem] = useState<string | null>(null);
  const [save, { isLoading }] = useBulkSetClientTeamMutation();

  const nameOf = (id: string) => {
    const user = team.find((u) => u.id === id);
    return user ? userName(user) : null;
  };
  const summary = bulkSummary(
    customerIds.length,
    { set: owner !== KEEP, name: owner === KEEP ? null : nameOf(owner) },
    { set: manager !== KEEP, name: manager === KEEP ? null : nameOf(manager) },
  );
  const nothing = owner === KEEP && manager === KEEP;

  const onSave = async () => {
    setProblem(null);
    try {
      const result = await save({
        orgId,
        customerIds,
        setOwner: owner !== KEEP,
        ownerUserId: owner === KEEP || !owner ? null : owner,
        setManager: manager !== KEEP,
        managerUserId: manager === KEEP || !manager ? null : manager,
      }).unwrap();
      toast.success(
        result.updated === 1
          ? "Client team set for 1 client."
          : `Client team set for ${result.updated} clients.`,
      );
      onDone?.();
      onClose();
    } catch (error) {
      setProblem(errorText(error));
    }
  };

  const picker = (
    id: string,
    label: string,
    value: string,
    onChange: (value: string) => void,
  ) => (
    <div className={"flex flex-col gap-1.5"}>
      <label htmlFor={id} className={"text-[13px] font-bold text-[#1F2433]"}>
        {label}
      </label>
      <select
        id={id}
        className={pickerClass}
        value={value}
        onChange={(e) => onChange(e.target.value)}>
        <option value={KEEP}>Keep as is</option>
        <option value={""}>Remove (not set)</option>
        {team.map((user) => (
          <option key={user.id} value={user.id}>
            {userName(user)}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <PortalModal
      title={"Set client team"}
      subtitle={
        customerIds.length === 1
          ? "For the selected client."
          : `For the ${customerIds.length} selected clients.`
      }
      onClose={onClose}
      width={480}
      footer={
        <>
          <button type={"button"} className={modalCancel} onClick={onClose}>
            Cancel
          </button>
          <button
            type={"button"}
            className={modalPrimary}
            disabled={isLoading || nothing}
            onClick={onSave}>
            {isLoading ? "Saving…" : "Save"}
          </button>
        </>
      }>
      <div className={"flex items-center gap-1.5 text-[13px] text-[#6B7185]"}>
        <span>What do they get?</span>
        <ClientTeamInfoButton />
      </div>
      {picker("bulk-client-owner", "Client owner", owner, setOwner)}
      {picker("bulk-client-manager", "Client manager", manager, setManager)}
      <div className={"text-[13px] font-semibold text-[#3A4256]"}>
        {summary}
      </div>
      {problem && (
        <div
          role={"alert"}
          className={
            "rounded-[9px] border border-[#FDA29B] bg-[#FEF3F2] px-3 py-2 text-[13px] text-[#B42318]"
          }>
          {problem}
        </div>
      )}
    </PortalModal>
  );
};
